import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Outlet } from 'react-router-dom';

import NavBar from "../components/NavBar";

import Footer from "../components/Footer";
import ReCAPTCHA from 'react-google-recaptcha';

import { useLocation } from "react-router-dom";
import ScrollToTop from "../components/ScrollToTop";
import MobileMenu from "../components/MobileMenu";

const MainLayout = () => {
    const { t } = useTranslation();

    const scrollContactRef = useRef(null);


    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
    });

    const [captchaToken, setCaptchaToken] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    const [menuOpen, setMenuOpen] = useState(false);
    const [whoOpen, setWhoOpen] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!captchaToken) {
            alert(t('form.captchaAlert'));
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_RECAPTCHA_VERIFY_ENDPOINT}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    captchaToken: captchaToken,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    companyName: formData.companyName,
                }),
            });

            const result = await response.json();
            if (response.ok) {
                console.log("CAPTCHA verified successfully:", result);
                setIsSubmitted(true);
                setSubmitMessage(t('form.successMessage'));
            } else {
                console.error("Something went wrong", response);
                setSubmitMessage(t('form.errorMessage'));
            }
        } catch (error) {
            console.error("Error during CAPTCHA verification:", error);
            setSubmitMessage(t('form.errorMessage'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const location = useLocation();

    // Hide form and "book demo button" on demo page
    const isDemoPage = location.pathname === "/demo";

    const [showStickyNav, setShowStickyNav] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowStickyNav(true);
            } else {
                setShowStickyNav(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);


    return (
        <>
            <div
                className={`pt-4 min-h-screen ${isDemoPage
                    ? "bg-gradient-to-b from-[#9B29FF] to-[#671BFF]"
                    : "bg-[#f2f4f8]"
                    }`}
            >
                <div className="max-w-screen-2xl 2xl:mx-auto 2xl:px-8 sm:mx-8 mx-4">

                    <div className="mb-4">

                        <NavBar
                            isDemoPage={isDemoPage}
                            location={location}
                            menuOpen={menuOpen}
                            setMenuOpen={setMenuOpen}
                            whoOpen={whoOpen}
                            setWhoOpen={setWhoOpen}
                        />
                    </div>

                    {showStickyNav && (
                        <div className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-md shadow-md">
                            <div className="max-w-screen-2xl 2xl:mx-auto 2xl:px-8 sm:mx-8 mx-4">
                                <NavBar
                                    isDemoPage={isDemoPage}
                                    location={location}
                                    menuOpen={menuOpen}
                                    setMenuOpen={setMenuOpen}
                                    whoOpen={whoOpen}
                                    setWhoOpen={setWhoOpen}
                                    isSticky={true}
                                />
                            </div>
                        </div>
                    )}

                    {/* Mobile menu drawer sits outside NavBar */}
                    {menuOpen && (
                        <MobileMenu
                            menuOpen={menuOpen}
                            setMenuOpen={setMenuOpen}
                            whoOpen={whoOpen}
                            setWhoOpen={setWhoOpen}
                            location={location}
                            isDemoPage={isDemoPage}
                        />
                    )}

                    <ScrollToTop />
                    <Outlet />
                </div>

                <form
                    id="demo"
                    ref={scrollContactRef}
                    onSubmit={handleSubmit}
                    className="relative w-full bg-no-repeat bg-cover bg-top overflow-hidden pt-4"
                    style={{ backgroundImage: `${isDemoPage ? `url(${require("../media/images/stars-bottom.png")})` : `url(${require("../media/images/Horizon.png")})`}` }}
                >

                    <div className="flex flex-col justify-center relative max-w-screen-sm z-20 mx-8 sm:mx-auto">

                        {!isDemoPage && (
                            <>
                                <h2 className="text-6xl mt-36 mb-6 text-center">{t('form.letsTalk')}</h2>
                                <p className="text-center text-lg text-white mb-14 font-semibold">
                                    {t('form.formSubtitle')}
                                </p>
                            </>
                        )}

                        <div className="flex flex-col space-y-10 max-w-[5   00px] mx-auto">
                            <div className="flex space-x-10">
                                {/* First Name Input with White Border */}
                                <div className="relative w-full">
                                    <div className="absolute inset-[-15px] border border-white rounded-full"></div> {/* White border */}
                                    <input
                                        type="text"
                                        placeholder={t('form.firstName')}
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        name="firstName"
                                        className="relative px-6 py-2 text-[--primary-dark] text-sm sm:text-base font-medium tracking-wide bg-white rounded-full w-full z-10 h-12"
                                        required
                                    />
                                </div>

                                {/* Last Name Input with White Border */}
                                <div className="relative w-full">
                                    <div className="absolute inset-[-15px] border border-white rounded-full"></div> {/* White border */}
                                    <input
                                        type="text"
                                        placeholder={t('form.lastName')}
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        name="lastName"
                                        className="relative px-6 py-2 text-[--primary-dark] text-sm sm:text-base font-medium tracking-wide bg-white rounded-full w-full z-10 h-12"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Email Address Input */}
                            <div className="relative w-full">
                                <div className="absolute inset-[-15px] border border-white rounded-full"></div> {/* White border */}
                                <input
                                    type="email"
                                    placeholder={t('form.email')}
                                    value={formData.email}
                                    onChange={handleChange}
                                    name="email"
                                    className="relative px-6 py-2 text-[--primary-dark] text-sm sm:text-base font-medium tracking-wide bg-white rounded-full w-full z-10 h-12"
                                    required
                                />
                            </div>

                            {/* Company Name Input */}
                            <div className="relative w-full">
                                <div className="absolute inset-[-15px] border border-white rounded-full"></div> {/* White border */}
                                <input
                                    type="text"
                                    placeholder={t('form.companyName')}
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    name="companyName"
                                    className="relative px-6 py-2 text-[--primary-dark] text-sm sm:text-base font-medium tracking-wide bg-white rounded-full w-full z-10 h-12"
                                    required
                                />
                            </div>

                        </div>

                    </div>

                    <div className="flex flex-col items-center justify-center mb-10 relative max-w-[530px] mx-8 sm:mx-auto">

                        <div className="flex flex-col sm:flex-row items-center w-full justify-between">
                            <div className="mt-12 z-20">
                                <ReCAPTCHA
                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                    onChange={handleCaptchaChange}
                                />
                            </div>

                            <button
                                type="submit"
                                className={`mt-12 relative px-6 py-2 text-[--primary-light] text-base font-medium tracking-wide bg-[--primary-dark] rounded-full border-2 border-white 
								before:absolute before:inset-[-20px] before:rounded-full before:border before:border-white before:content-[''] z-10
								${isSubmitting || isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
                                disabled={isSubmitting || isSubmitted}
                            >
                                {isSubmitting ? t('form.submitting') : t('form.bookDemo')}
                            </button>
                        </div>

                        {submitMessage && <p className="text-center text-lg mt-4 text-white z-10">{submitMessage}</p>}

                    </div>
                    {/*<div className="hidden lg:block relative z-[5] w-full max-w-screen-lg mx-auto -mt-20">
                        <img className="opacity-0 2xl:opacity-100 relative z-0 w-full rounded-t-xl" src={require("../media/images/background-block2.jpg")}></img>
                    </div>*/}

                    <div className="pointer-events-none absolute bottom-0 left-0 w-full h-96
				                        bg-gradient-to-b from-transparent to-[#6100cf]" >
                    </div>

                    <img className="hidden lg:block lg:absolute bottom-20 left-20 z-10 w-24" src={require("../media/images/ISO-27001-2022-grey_no background1.png")} alt="ISO-27001-2022" />

                    <Footer />
                </form>

            </div>
        </>
    );
};

export default MainLayout;
